import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { gray } from '../../../theme/themePrimitives';
import ImageClass from '../../../assets';
import { SearchOutlined } from '@mui/icons-material';
import { useParams } from 'react-router';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import { BusinessReviewsObject, ImportReviewParams } from '../../../params/redux/review';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { importReviewsThunk, searchBusinessReviewsThunk } from '../../../redux/thunks/review';
import { useDispatch, useSelector } from 'react-redux';

interface BusinessRow {
    id: string;
    name: string;
    address: string;
    image: string;
    photo_reference: string;
}

const GetReviewModal = ({ open, onClose, openImportingModal }: { open: boolean; onClose: () => void; openImportingModal: () => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const [searchValue, setSearchValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { searchedBusiness } = useSelector((state: RootState) => state.review);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

    const rows = searchedBusiness.map((teamMember: BusinessReviewsObject) => ({
        id: teamMember.place_id,
        name: teamMember.name,
        address: teamMember.formatted_address,
        image: `https://maps.googleapis.com/maps/api/place/photo?maxwidth=500&photo_reference=${teamMember.photo_reference}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
        photo_reference: teamMember.photo_reference
    })) || [];

    const columns: GridColDef<BusinessRow>[] = [
        {
            field: 'name',
            headerName: 'BUSINESS INFO',
            flex: 1,
            renderCell: (params: GridRenderCellParams<BusinessRow, string>) => <Box display="flex" gap={2} alignItems="center" my={2}>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <img src={ImageClass.userPlacedholder} alt="User placehodler" height="25px" style={{
                        borderRadius: '50%'
                    }} />
                </Box>
                <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">{params.value}</Typography>
            </Box>
        },
        {
            field: 'address',
            headerName: 'LOCATION',
            flex: 1
        }
    ];

    const searchBusinesses = async () => {
        setIsLoading(true);
        try {
            await dispatch(searchBusinessReviewsThunk(searchValue)).unwrap();
        } catch (e) {
            // Catch errors
        } finally {
            setIsLoading(false);
        }
    };

    const importReviews = async () => {
        try {
            const selectedBusinesses = searchedBusiness.filter((business: BusinessReviewsObject) => selectedRows.includes(business.place_id));
            const formattedReview: ImportReviewParams[] = selectedBusinesses.map((business: BusinessReviewsObject) => ({
                place_id: business.place_id,
                location_coordinate: `${business.latitude}, ${business.longitude}`,
                name: business.name,
                formatted_address: business.formatted_address,
                photo_reference: business.photo_reference
            }));
            await dispatch(importReviewsThunk({ data: formattedReview, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            onClose();
            openImportingModal();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={onClose} title="Get Reviews">
        <Box display="flex" flexDirection="column" gap={2}>
            <CustomInput
                label="Search location and import reviews into Gonzy"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search..."
                fullWidth
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlined />
                            </InputAdornment>
                        )
                    }
                }}
            />
            <Box maxHeight="300px" overflow="auto">
                <DataGrid
                    rows={rows as BusinessRow[]}
                    loading={isLoading}
                    columns={columns}
                    // pagination
                    onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => setSelectedRows(newSelection)}
                    checkboxSelection
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20
                            }
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row--borderBottom': {
                            backgroundColor: `${gray[200]} !important`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '14px'
                        },
                        'width': '100%',
                        'overflowX': 'hidden'
                    }}
                />
            </Box>
            {/* Action buttons for cancel and submit */}
            <Box display="flex" gap={2} mt={2}>
                <Button variant="outlined" color="primary" onClick={onClose} fullWidth>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" onClick={() => (selectedRows.length > 0 ? importReviews() : searchBusinesses())} fullWidth disabled={false}>
                    {selectedRows.length > 0 ? 'Import Reviews' : 'Search'}
                </Button>
            </Box>
        </Box>
    </CustomModal>;
};

export default GetReviewModal;